import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchQueueMessages,
  deleteQueueMessage,
  clearQueue,
  fetchBlobs,
  downloadBlobUrl,
} from "../api/client";
import { useAuth } from "../auth/useAuth";
import { canManageQueue } from "../auth/permissions";
import type {
  QueueMessage,
  QueueMessagesResponse,
  BlobGroup,
  BlobListResponse,
  QueueStatus,
} from "../api/types";
import { useToast } from "../context/ToastContext";
import { QueueStatusFilter, QUEUE_STATUS_LABEL } from "../lib/QueueStatusFilter";
import { PreviewModal, fileIcon as sharedFileIcon, buildPreviewData, resolveContentType } from "../components/AttachmentPreview";
import type { PreviewData } from "../components/AttachmentPreview";
import { ConfirmModal } from "../components/ConfirmModal";

export function QueuePage() {
  return (
    <div>
      <h1 className="page-title">
        <span className="material-symbols-outlined icon-lg" style={{ marginRight: "var(--space-2)" }}>outbox</span>
        Coda Output
      </h1>
      <QueueTab />
    </div>
  );
}

// ────────────────────────── QUEUE TAB ──────────────────────────

function QueueTab() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const canWrite = canManageQueue(user?.roles ?? []);
  const [data, setData] = useState<QueueMessagesResponse | null>(null);
  const [blobData, setBlobData] = useState<BlobListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Paginazione (punto 4, 2026-07-14): la lista viene ora da DB, non piu' da
  // un peek limitato della coda Azure — puo' crescere illimitatamente (resta
  // per sempre, anche gli elementi gia' prelevati), va paginata.
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  // Punto 9 (2026-07-15): default mostra solo i "da prelevare" (non ancora
  // prelevati dal consumer esterno). Toggle per rivedere anche i già prelevati.
  const [status, setStatus] = useState<QueueStatus>("pending");

  // Attachment preview state
  const [previewing, setPreviewing] = useState<PreviewData | null>(null);
  const [previewRenderError, setPreviewRenderError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    Promise.all([
      fetchQueueMessages(page, PAGE_SIZE, status),
      fetchBlobs(1, 100),
    ])
      .then(([q, b]) => { setData(q); setBlobData(b); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  // Build map: mailPath -> BlobGroup
  const blobsByMailPath = new Map<string, BlobGroup>();
  for (const g of blobData?.groups ?? []) {
    blobsByMailPath.set(g.mail_path, g);
  }

  const handleClearQueue = async () => {
    try {
      const res = await clearQueue();
      const n = res.reopened?.length ?? 0;
      addToast("success", n > 0 ? `Coda svuotata — ${n} richieste riaperte` : "Coda svuotata");
      setConfirmClear(false);
      load();
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      const res = await deleteQueueMessage(messageId);
      if (res.already_picked_up) {
        addToast("info", "Il messaggio era già stato prelevato nel frattempo");
      } else {
        const n = res.reopened?.length ?? 0;
        addToast("success", n > 0 ? "Messaggio eliminato — richiesta riaperta" : "Messaggio eliminato dalla coda");
      }
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes("404")) {
        addToast("info", "Richiesta non trovata");
      } else {
        addToast("error", `Errore: ${msg}`);
      }
      setConfirmDeleteId(null);
      load();
    }
  };

  const handleDownloadJson = (msg: QueueMessage) => {
    const json = JSON.stringify(msg.content, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${msg.summary.mail_path || msg.message_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadBlob = async (blobPath: string, filename: string) => {
    try {
      const { url } = await downloadBlobUrl(blobPath);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      addToast("error", `Download fallito: ${(e as Error).message}`);
    }
  };

  const handlePreviewBlob = async (blobPath: string, filename: string, contentType: string) => {
    setPreviewRenderError(null);
    setPreviewLoading(filename);
    try {
      const { url } = await downloadBlobUrl(blobPath);
      const resolved = resolveContentType(contentType || "", filename);
      const { preview, renderError: re } = await buildPreviewData(url, resolved, filename);
      if (re) setPreviewRenderError(re);
      setPreviewing(preview);
    } catch (e) {
      addToast("error", `Preview fallita: ${(e as Error).message}`);
    } finally {
      setPreviewLoading(null);
    }
  };

  const closePreview = () => {
    if (previewing) URL.revokeObjectURL(previewing.url);
    setPreviewing(null);
    setPreviewRenderError(null);
  };

  const messages = data?.messages ?? [];

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)", flexWrap: "wrap", rowGap: "var(--space-2)" }}>
        <span className="text-muted" style={{ fontSize: "var(--font-size-sm)", minWidth: 0 }}>
          {data ? `${data.total} ${QUEUE_STATUS_LABEL[status]}` : "Caricamento..."}
          {data?.queue_name ? ` (${data.queue_name})` : ""}
        </span>
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          <QueueStatusFilter value={status} counts={data?.counts} onChange={(s) => { setPage(1); setStatus(s); }} />
          {messages.length > 0 && canWrite && (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmClear(true)}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete_sweep</span>
              Svuota coda
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={isLoading}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>
            Aggiorna
          </button>
        </div>
      </div>

      {(status === "in_lease" || messages.some((m) => m.state === "in_lease")) && (
        <div className="text-muted text-xs" style={{ marginBottom: "var(--space-3)" }}>
          In lavorazione: il gestionale del cliente ha preso in carico la pratica. Se non la conferma
          entro il tempo del lease, torna in coda da sola.
        </div>
      )}

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)", padding: "var(--space-3)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
          Errore: {error}
        </div>
      )}

      {data?.peek_incomplete && (
        <div className="card" style={{ background: "var(--indaco-warning-light)", color: "var(--indaco-warning)", padding: "var(--space-3)", marginBottom: "var(--space-3)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4, verticalAlign: "middle" }}>warning</span>
          Ci sono più di 32 messaggi in coda: sopra questa soglia lo stato "in lavorazione" non è determinabile per singolo messaggio e tutto è mostrato come "in coda".
        </div>
      )}

      {data && messages.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, display: "block", marginBottom: "var(--space-2)", opacity: 0.4 }}>inbox</span>
          Coda vuota &mdash; tutti i messaggi sono stati consumati
        </div>
      )}

      {/* Message cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", opacity: isLoading && data ? 0.6 : 1 }}>
        {messages.map((msg) => {
          const s = msg.summary;
          const isExpanded = expandedId === msg.message_id;
          const group = s.mail_path ? blobsByMailPath.get(s.mail_path) : undefined;
          const attachments = group?.blobs.filter((b) => b.name !== "output.json" && b.size > 0) ?? [];

          return (
            <div key={msg.message_id} className="card" style={{ padding: 0, overflow: "hidden" }}>
              {/* Ticket info */}
              <div style={{ padding: "var(--space-3)" }}>
                {/* Row 1: id chip + edificio + priorita + link ticket */}
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-1)" }}>
                  {s.request_id && (
                    <span style={{ fontFamily: "var(--font-family-mono)", fontSize: "var(--font-size-xs)",
                      background: "var(--indaco-gray-100)", color: "var(--indaco-gray-600)",
                      padding: "1px 6px", borderRadius: 4, flexShrink: 0 }}>
                      {s.request_id.slice(0, 6)}
                    </span>
                  )}
                  {s.edificio && (
                    <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-base)", flex: 1 }}>
                      {s.edificio}
                    </span>
                  )}
                  {s.priorita && (
                    <span className={`badge ${s.priorita === "alta" ? "badge-danger" : s.priorita === "media" ? "badge-warning" : "badge-neutral"}`}>
                      {s.priorita}
                    </span>
                  )}
                  {s.tipologia && (
                    <span className="badge badge-info">{s.tipologia}</span>
                  )}
                  {msg.state === "in_lease" && (
                    <span className="badge badge-warning"
                      title={`In lavorazione dal ${msg.in_lease_since ? formatDate(msg.in_lease_since) : "?"} — il gestionale del cliente l'ha presa in carico: se non la conferma entro il lease, la pratica torna in coda.`}>
                      In lavorazione
                    </span>
                  )}
                  {msg.picked_up_at && (
                    <span className="badge badge-neutral" title={`Non più in coda dal ${formatDate(msg.picked_up_at)}`}>
                      Prelevato
                    </span>
                  )}
                  {s.request_id && (
                    <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px", flexShrink: 0 }}
                      title="Apri ticket nella coda di lavoro"
                      onClick={() => navigate(`/requests/${s.request_id}`)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 15, marginRight: 4 }}>open_in_new</span>
                      Ticket
                    </button>
                  )}
                </div>

                {/* Row 2: indirizzo */}
                {s.indirizzo && (
                  <div className="text-sm text-muted" style={{ marginBottom: "var(--space-1)" }}>
                    {s.indirizzo}
                  </div>
                )}

                {/* Row 3: griglia dati ticket */}
                <div style={{
                  display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px var(--space-3)",
                  fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)",
                  marginTop: "var(--space-2)",
                }}>
                  {s.codice_impianto && (
                    <>
                      <span className="text-muted">Impianto</span>
                      <span>
                        {s.tipo_impianto ? `${s.tipo_impianto} \u00b7 ${s.codice_impianto}` : s.codice_impianto}
                        {s.confidence_impianto != null && <ConfBadge value={s.confidence_impianto} />}
                      </span>
                    </>
                  )}
                  {s.chiamante && (
                    <>
                      <span className="text-muted">Chiamante</span>
                      <span>
                        {s.chiamante}
                        {s.confidence_chiamante != null && <ConfBadge value={s.confidence_chiamante} />}
                      </span>
                    </>
                  )}
                  {s.motivo && (
                    <>
                      <span className="text-muted">Motivo</span>
                      <span>{s.motivo.length > 150 ? s.motivo.slice(0, 150) + "\u2026" : s.motivo}</span>
                    </>
                  )}
                  {s.note && (
                    <>
                      <span className="text-muted">Note</span>
                      <span>{s.note}</span>
                    </>
                  )}
                  {s.nota_operatore && (
                    <>
                      <span className="text-muted">Nota operatore</span>
                      <span>{s.nota_operatore}</span>
                    </>
                  )}
                  {s.data_origine && (
                    <>
                      <span className="text-muted">Data origine</span>
                      <span>{formatDate(s.data_origine)}</span>
                    </>
                  )}
                </div>

                {/* Documenti email dal blob */}
                {attachments.length > 0 && (
                  <div style={{
                    background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-md)",
                    padding: "var(--space-2)", marginBottom: "var(--space-2)",
                  }}>
                    <div className="text-sm text-muted" style={{ marginBottom: "var(--space-1)", fontWeight: "var(--font-weight-medium)" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>folder_open</span>
                      Documenti email ({attachments.length} file)
                    </div>
                    {attachments.map((att) => (
                      <div key={att.name} style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", padding: "2px 0" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }}>{sharedFileIcon(att.content_type || "", att.name)}</span>
                        <span className="text-sm" style={{ flex: 1, cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                          onClick={() => handlePreviewBlob(`${s.mail_path}/${att.name}`, att.name, att.content_type || "")}>
                          {att.name}
                          {previewLoading === att.name && <span className="spinner" style={{ width: 12, height: 12, marginLeft: 4, verticalAlign: "middle" }} />}
                        </span>
                        <span className="text-muted" style={{ fontSize: "var(--font-size-xs)" }}>{formatSize(att.size)}</span>
                        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 6px" }} title="Scarica"
                          onClick={() => handleDownloadBlob(`${s.mail_path}/${att.name}`, att.name)}>
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>download</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer: mailPath + date */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
                  <span style={{ fontFamily: "var(--font-family-mono)", fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-400)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                    {s.mail_path}
                  </span>
                  {msg.inserted_on && (
                    <span className="text-muted" style={{ fontSize: "var(--font-size-xs)", whiteSpace: "nowrap" }}>
                      In coda dal {formatDate(msg.inserted_on)}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: "var(--space-2)", borderTop: "1px solid var(--indaco-gray-100)", paddingTop: "var(--space-2)" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setExpandedId(isExpanded ? null : msg.message_id)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{isExpanded ? "visibility_off" : "data_object"}</span>
                    {isExpanded ? "Nascondi JSON" : "Vedi JSON"}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleDownloadJson(msg)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
                    Scarica JSON
                  </button>
                  {canWrite && !msg.picked_up_at && (
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: "auto" }} onClick={() => setConfirmDeleteId(msg.message_id)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                      Elimina
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded JSON */}
              {isExpanded && (
                <pre style={{
                  margin: 0, padding: "var(--space-3)",
                  background: "var(--indaco-gray-50)", borderTop: "1px solid var(--indaco-gray-200)",
                  fontSize: "var(--font-size-xs)", overflow: "auto", maxHeight: 400,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {JSON.stringify(msg.content, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="pagination">
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
          </button>
          <span className="text-muted text-sm">
            Pagina {page} di {Math.max(1, Math.ceil(data.total / PAGE_SIZE))} &mdash; {data.total} totali
          </span>
          <button className="btn btn-ghost btn-sm" disabled={page * PAGE_SIZE >= data.total} onClick={() => setPage((p) => p + 1)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
          </button>
        </div>
      )}

      {confirmClear && (
        <ConfirmModal
          title="Svuota coda"
          message={`Vuoi davvero svuotare la coda? Tutti i ${messages.length} messaggi verranno eliminati.`}
          confirmLabel="Svuota"
          danger
          onConfirm={handleClearQueue}
          onCancel={() => setConfirmClear(false)}
        />
      )}
      {confirmDeleteId && (
        <ConfirmModal
          title="Elimina messaggio"
          message="Vuoi davvero eliminare questo messaggio dalla coda?"
          confirmLabel="Elimina"
          danger
          onConfirm={() => handleDeleteMessage(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
      {previewing && <PreviewModal data={previewing} renderError={previewRenderError} onClose={closePreview} />}
    </>
  );
}

// ────────────────────────── HELPERS ──────────────────────────

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ConfBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const cls = pct >= 80 ? "badge-success" : pct >= 50 ? "badge-warning" : "badge-danger";
  return (
    <span className={`badge ${cls}`} style={{ marginLeft: 6, fontSize: "var(--font-size-xs)" }}>
      {pct}%
    </span>
  );
}


